import { Controller } from "stimulus"

/**
 * Star rating input controller.
 *
 * Manages a 1-5 star rating widget backed by a hidden input field.
 * Uses radio group ARIA pattern with aria-checked and roving tabindex
 * for keyboard navigation (arrow keys, Home, End, Space, Enter).
 *
 * Targets:
 *   input - hidden form field holding the integer value
 *   star  - each clickable star button (expects data-value attribute)
 *   label - optional text label showing "X/5" or "Not rated"
 */
export default class extends Controller {
  static targets = ["input", "star", "label"]

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.starTargets.forEach((star) => {
      star.addEventListener("keydown", this.boundHandleKeydown)
    })

    const currentValue = parseInt(this.inputTarget.value, 10) || 0
    this.render(currentValue)
  }

  disconnect() {
    if (!this.boundHandleKeydown) return

    this.starTargets.forEach((star) => {
      star.removeEventListener("keydown", this.boundHandleKeydown)
    })
  }

  select(event) {
    const button = event.currentTarget
    const value = parseInt(button.dataset.value, 10)
    this.inputTarget.value = value
    this.render(value)

    const selectedStar = this.starTargets[value - 1]
    if (selectedStar) selectedStar.focus()
  }

  handleKeydown(event) {
    const currentIndex = this.starTargets.indexOf(event.currentTarget)
    if (currentIndex === -1) return

    let nextIndex = currentIndex

    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        nextIndex = Math.min(currentIndex + 1, this.starTargets.length - 1)
        break
      case "ArrowLeft":
      case "ArrowDown":
        nextIndex = Math.max(currentIndex - 1, 0)
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = this.starTargets.length - 1
        break
      case " ":
      case "Enter": {
        event.preventDefault()
        const value = currentIndex + 1
        this.inputTarget.value = value
        this.render(value)
        this.starTargets[currentIndex].focus()
        return
      }
      default:
        return
    }

    event.preventDefault()
    const value = nextIndex + 1
    this.inputTarget.value = value
    this.render(value)
    this.starTargets[nextIndex].focus()
  }

  render(value) {
    const checkedValue = value > 0 ? value : 1

    this.starTargets.forEach((star, index) => {
      const starValue = index + 1
      const span = star.querySelector("span")
      const selected = starValue <= value
      const checked = starValue === value

      if (span) {
        span.classList.toggle("text-amber-400", selected)
        span.classList.toggle("text-gray-300", !selected)
      }

      star.setAttribute("aria-checked", checked ? "true" : "false")
      star.setAttribute("tabindex", starValue === checkedValue ? "0" : "-1")
      star.setAttribute("aria-label", starValue + " star" + (starValue > 1 ? "s" : ""))
    })

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = value > 0 ? value + "/5" : "Not rated"
    }
  }
}
